<template>
    <div class="flex flex-col space-y-5">
        <div class="flex justify-center" v-if="title">
            <h1 class="text-2xl">{{title}}</h1>
        </div>

        <div class="flex items-center gap-1 text-sm md:gap-4 flex-wrap" v-show="showFilter">
            <div v-for="filter in filters"
                 :class="{'bg-primary border-primary text-white': filter.active, 'border-slate-300 bg-white hover:outline-1 outline-primary': !filter.active}"
                 class="border-1 px-4 py-2 rounded-xl cursor-pointer transition-all duration-100 course-list-filter-button"
                 :data-filterValue="filter.value"
                 @click="filterValue = filter.value"
            >
                {{ filter.label }}
            </div>
        </div>
        <div class="relative">
            <div 
                ref="tableBodyWrapper" 
                class="overflow-y-hidden transition-all duration-500 ease-in-out text-sm md:text-base"
                :style="{ maxHeight: tableMaxHeight }"
            >
                <table class="table-auto text-left w-full">
                    <thead>
                    <tr class="text-base md:text-lg" ref="tableHeader">
                        <th class="font-normal py-2 pl-1 hidden md:table-cell">{{ $t('Klasse') }}</th>
                        <th class="font-normal py-2 pl-1">{{ $t('Oppstart') }}</th>
                        <th class="font-normal py-2 pl-1">{{ $t('Kurs') }}</th>
                        <th class="font-normal py-2 pl-1">{{ $t('Pris') }}</th>
                        <th class="text-right font-normal py-2 pl-1">{{ $t('Påmelding') }}</th>
                    </tr>
                    </thead>
                    <tbody ref="tableBody" class="space-y-4">
                    <tr v-for="course in courseList" class="border-b-1 border-slate-300 hover:border-primary">
                        <td class="py-2 pl-1 hidden md:table-cell">{{ course.classShortName }}</td>
                        <td class="py-2 pl-1">{{ course.formattedStartDate }}</td>
                        <td class="py-2 pl-1">{{ course.courseName }}</td>
                        <td class="py-2 pl-1 text-nowrap">{{ moneyFormat(course.price) }}</td>
                        <td class="text-right py-2 pl-1">
                            <div class="flex items-center justify-end">
                                <div v-if="shouldShowBookingButton(course)"
                                     class="flex gap-2 text-white bg-primary py-1 px-2 md:px-4 md:py-2 rounded-lg md:rounded-xl hover:bg-primary-dark transition-all duration-200"
                                     :class="{ 'opacity-50 cursor-not-allowed': isLoadingCourse(course), 'cursor-pointer': !isLoadingCourse(course) }"
                                     @click="openBookingModal(course)"
                                >
                                    <div>{{ $t('Påmelding') }}</div>
                                    <ArrowRight v-if="!isLoadingCourse(course)" :strokeWidth="1"/>

                                    <LoaderCircle v-if="isLoadingCourse(course)"
                                                  class="animate-spin"
                                    />
                                </div>
                                <div v-if="!shouldShowBookingButton(course)"
                                     class="flex gap-2 rounded-lg md:rounded-xl bg-white border border-slate-300 py-1 px-2 md:px-4 md:py-2 cursor-not-allowed text-slate-400">
                                    {{ $t('Fullt') }}
                                </div>
                            </div>
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>
            <div v-if="!isExpanded && courseList.length > 10" class="mt-4 flex justify-center">
                <button 
                    @click="expandTable"
                    class="px-6 py-2 bg-primary hover:scale-105 text-white rounded-xl hover:bg-primary-dark transition-all duration-200 cursor-pointer flex items-center gap-2"
                >
                    {{ $t('Vis alle') }} <ArrowDown :strokeWidth="1" />
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
    import {ref, computed, watch, nextTick, onBeforeMount} from 'vue';
    import axios from "axios";
    import {ArrowRight, ArrowDown, LoaderCircle} from 'lucide-vue-next'

    const { groupBy, initFilterValue } = defineProps({
        title: {
            type: String,
            required: false,
            default: null
        },
        groupBy: {
            type: String,
            required: false,
            default: 'category'
        },
        showFilter: {
            type: Boolean,
            required: false,
            default: true
        },
        initFilterValue: {
            type: [String, Number, null],
            required: false,
            default: null
        }
    })

    const courseGroupApiResult = ref([]);
    const isExpanded = ref(false);
    const tableHeader = ref(null);
    const tableBody = ref(null);
    const tableBodyWrapper = ref(null);
    const tableMaxHeight = ref('auto');

    const loadingCourse = ref(null);
    
    const moneyFormat = (price) => {
        return price ? window.TABS.L10n.money.parse(price).format(0) : '-';
    }

    const shouldShowBookingButton = (course) => {
        // Placeholder logic for showing booking button
        return !course.attendees || course.attendees < course.maxAttenders;
    }

    const calculateInitialHeight = () => {
        nextTick(() => {
            if (!isExpanded.value && tableBody.value && courseList.value.length > 10) {
                // Calculate the height of 10 rows
                const rows = tableBody.value.querySelectorAll('tr');

                let height = 0;
                for (let i = 0; i < 10; i++) {
                    height += rows[i].offsetHeight;
                }
                height += tableHeader.value.offsetHeight;
                tableMaxHeight.value = `${height}px`;
            } else {
                tableMaxHeight.value = 'auto';
            }
        });
    };

    const expandTable = () => {
        if (tableBodyWrapper.value) {
            const fullHeight = tableBodyWrapper.value.scrollHeight;
            tableMaxHeight.value = `${fullHeight}px`;
            isExpanded.value = true;
        }
    };

    const filterValue = ref(initFilterValue);
    const filters = computed(() => {
        if (!courseGroupApiResult.value.length) {
            return [];
        }
        return [{
            label: window.TABS.$t('Alle'),
            value: null,
            active: filterValue.value === null
        }, ...courseGroupApiResult.value.map((group, i) => {
            let groupValue = i;
            if (groupBy === 'class') {
                groupValue = group.class_id;
            }

            return {
                label: group.short_name || group.name,
                value: groupValue,
                active: filterValue.value === groupValue
            }
        })]
    })

    const courseList = computed(() => {
        const courses = []
        if (filterValue.value === null) {
            courseGroupApiResult.value.map(group => {
                group.courses.map(course => {
                    course.courseEvents.map(courseEvent => courses.push(courseEvent.courseEvent))
                })
            })
        } else {
            let filteredCourses = courseGroupApiResult.value.filter((group, i) => {
                if (groupBy === 'class') {
                    return group.class_id === filterValue.value
                }
                return i === filterValue.value
            })
            filteredCourses.map(group => {
                group.courses.map(course => {
                    course.courseEvents.map(courseEvent => courses.push(courseEvent.courseEvent))
                })
            })
        }

        return courses.sort((a, b) => {
            return a.startDateTimestamp - b.startDateTimestamp;
        })
    })

    // Watch for changes in courseList or filter to recalculate height
    watch([courseList, filterValue], () => {
        calculateInitialHeight();
    });

    onBeforeMount(async () => {
        if (window.courseListData) {
            courseGroupApiResult.value = window.courseListData.courseGroups || [];
        } else {
            const urlParams = new URLSearchParams(window.location.search);

            try {
                const response = await axios.get('/api/v2/course', {
                    params: {
                        officeId: window.officeId,
                        productId: urlParams.get('productId') || urlParams.get('product_id') || urlParams.get('product'),
                        groupBy: urlParams.get('groupBy') || groupBy,
                        classId: urlParams.get('classId') || urlParams.get('class_id') || urlParams.get('class'),
                        categoryId: urlParams.get('categoryId') || urlParams.get('category_id') || urlParams.get('category'),

                    },
                })
                courseGroupApiResult.value = response.data.courseGroups || [];
            } catch (error) {
                console.error('Error fetching course data:', error);
            }
        }
        calculateInitialHeight();
    })



    function openBookingModal(course) {
        if (loadingCourse.value) {
            return;
        }

        loadingCourse.value = {courseId: course.courseId, classId: course.classId};


        TABS.api.loadCourse(null, course.courseId, course.classId, course.officeId, (html) => {
            // Callback after loading course
            loadingCourse.value = null;

            TABS.api.openModal(html);
            TABS.analytics.triggerAnalyticsEvent('courses', 'open', course.courseName);
        })
    }
    function isLoadingCourse(course) {
        return loadingCourse.value && loadingCourse.value.courseId === course.courseId && loadingCourse.value.classId === course.classId;
    }
</script>